import { useState } from "react";
import { Link } from "react-router-dom";
import { BrickWall, ScanLine, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import ConfirmDialog from "@/components/ConfirmDialog";
import FallbackImage from "@/components/FallbackImage";
import UploadBricksModal from "./UploadBricksModal";
import BrickTypeToggle, { BRICK_TYPE_LABELS } from "./BrickTypeToggle";
import useOrganization from "../../hooks/useOrganization";
import { updateBrickSeries } from "../../utils";
import type { Brick, BrickSeries, BrickType } from "../../masterImages/types";

interface Props {
  brickSeries: BrickSeries;
  onChange(): void;
}

type Filter = "all" | BrickType;

const PAGE_SIZE = 60;

// A brick without a type counts as full, as the old brick list showed it.
const typeOf = (brick: Brick): BrickType => (brick.type === "half" ? "half" : "full");

const BricksList: React.FC<Props> = ({ brickSeries, onChange }) => {
  const organization = useOrganization();
  const [showUpload, setShowUpload] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");
  const [shown, setShown] = useState(PAGE_SIZE);
  const bricks = brickSeries.bricks ?? [];
  const visible = filter === "all" ? bricks : bricks.filter((b) => typeOf(b) === filter);
  const count = (type: BrickType) => bricks.filter((b) => typeOf(b) === type).length;

  const save = async (next: Brick[]) => {
    try {
      await updateBrickSeries(organization, brickSeries._id, { bricks: next });
    } catch (e) {
      toast.error((e as Error).message);
    }
    onChange();
  };

  const deleteBrick = (brickId: string) => save(bricks.filter((b) => b._id !== brickId));

  const setType = (brickId: string, type: BrickType) =>
    save(bricks.map((b) => (b._id === brickId ? { ...b, type } : b)));

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Stenen</CardTitle>
          <CardDescription>
            {bricks.length} {bricks.length === 1 ? "steen" : "stenen"}: {count("full")} hele en{" "}
            {count("half")} halve. Klanten kiezen hieruit in de widget.
          </CardDescription>
        </div>
        <Button onClick={() => setShowUpload(true)}>
          <Upload />
          Stenen uploaden
        </Button>
      </CardHeader>
      <CardContent className="grid gap-4">
        {bricks.length > 0 && (
          <ToggleGroup
            type="single"
            value={filter}
            onValueChange={(next) => next && setFilter(next as Filter)}
            aria-label="Filter op type"
          >
            <ToggleGroupItem value="all">Alle {bricks.length}</ToggleGroupItem>
            <ToggleGroupItem value="full">Hele {count("full")}</ToggleGroupItem>
            <ToggleGroupItem value="half">Halve {count("half")}</ToggleGroupItem>
          </ToggleGroup>
        )}
        {bricks.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-10 text-center">
            <span className="flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <BrickWall className="size-6" />
            </span>
            <div>
              <p className="font-medium">Nog geen stenen</p>
              <p className="text-sm text-muted-foreground">
                Upload losse foto's van stenen, of knip ze uit een master image.
              </p>
            </div>
            <Button variant="outline" onClick={() => setShowUpload(true)}>
              <Upload />
              Stenen uploaden
            </Button>
          </div>
        ) : (
          <>
            <ul className="grid grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-3">
              {visible.slice(0, shown).map((brick) => (
                <li key={brick._id} className="overflow-hidden rounded-lg border bg-card">
                  <div className="checkerboard flex aspect-3/2 items-center justify-center p-3">
                    <FallbackImage
                      src={brick.displayImage}
                      fallback={brick.image}
                      loading="lazy"
                      className="max-h-full max-w-full object-contain drop-shadow-sm"
                    />
                  </div>
                  <div className="flex items-center gap-1 border-t p-2">
                    {brick.masterImage ? (
                      <>
                        <Badge variant="secondary">{BRICK_TYPE_LABELS[typeOf(brick)]}</Badge>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="ml-auto text-muted-foreground"
                          asChild
                        >
                          <Link
                            to={`/${organization}/series/${brickSeries._id}/masterImages/${brick.masterImage}`}
                            aria-label="Geknipt uit een master image: openen"
                            title="Geknipt uit een master image"
                          >
                            <ScanLine />
                          </Link>
                        </Button>
                      </>
                    ) : (
                      <>
                        <BrickTypeToggle
                          value={typeOf(brick)}
                          onChange={(type) => setType(brick._id, type)}
                        />
                        <ConfirmDialog
                          title="Steen verwijderen?"
                          description="De steen verdwijnt ook uit de widget."
                          confirmLabel="Verwijderen"
                          onConfirm={() => deleteBrick(brick._id)}
                          trigger={
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="ml-auto text-muted-foreground hover:text-destructive"
                              aria-label="Steen verwijderen"
                            >
                              <Trash2 />
                            </Button>
                          }
                        />
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {visible.length > shown && (
              <Button
                variant="outline"
                className="justify-self-center"
                onClick={() => setShown((n) => n + PAGE_SIZE)}
              >
                Meer tonen ({visible.length - shown})
              </Button>
            )}
          </>
        )}
      </CardContent>
      <UploadBricksModal
        brickSeries={brickSeries}
        open={showUpload}
        onReload={onChange}
        onClose={() => {
          setShowUpload(false);
          onChange();
        }}
      />
    </Card>
  );
};

export default BricksList;
